import swal from "sweetalert";

/**
 * The site's popup (`sweetalert`) with one default added: a MESSAGE popup — success or info, or one without an icon —
 * closes by itself after AUTO_CLOSE_MS (3 seconds). The look is in UiComponents/Scss/_swal.scss.
 *
 * Never closed by itself (the person has to answer or read them):
 *   - error and warning popups (the text is usually what went wrong),
 *   - questions: dangerMode, a list / object of buttons (Yes / No), or `content` (an input),
 *   - any popup that sets its own `timer` (that wins; `timer: 0` or `null` also turns it off).
 *
 * Use it exactly like swal:  import swal from "../../Shared/swal";  swal({ title, text, icon: "success" });
 * The popups of the live Brisk pages (BRISK_CPA_RSA/, girnarApi) still import "sweetalert" directly and are unchanged.
 */
export const AUTO_CLOSE_MS = 3000;
const AUTO_CLOSE_ICONS = ["success", "info"];

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

/** The options object of any way swal can be called: swal(opts) | swal(title) | swal(title, text, icon) | swal(title, opts). */
const optionsOf = (args) => {
  const [first, second, third] = args;
  if (isObject(first)) return first;
  const options = isObject(second) ? { ...second } : {};
  if (typeof first === "string" && options.title === undefined) options.title = first;
  if (typeof second === "string" && options.text === undefined) options.text = second;
  if (typeof third === "string" && options.icon === undefined) options.icon = third;
  return options;
};

/** True when a popup with these options is a message that may close itself. */
export const closesByItself = (options) => {
  if (!isObject(options)) return false;
  if ("timer" in options) return false; // its own choice, whatever it is
  if (options.dangerMode || options.content !== undefined) return false;
  const { buttons, icon } = options;
  if (Array.isArray(buttons) || isObject(buttons)) return false; // Yes / No, or named buttons: a question
  if (icon === undefined || icon === null || icon === "") return buttons === undefined || buttons === false;
  return AUTO_CLOSE_ICONS.includes(String(icon).toLowerCase());
};

/** The arguments to hand to swal: the same, plus `timer` when the popup is a message. */
export const withAutoClose = (args) => {
  if (!closesByItself(optionsOf(args))) return args;
  const [first, second, third] = args;
  if (isObject(first)) return [{ ...first, timer: AUTO_CLOSE_MS }, ...args.slice(1)];
  if (isObject(second)) return [first, { ...second, timer: AUTO_CLOSE_MS }, ...args.slice(2)];
  // swal("title", "text", "icon") / swal("title"): the long form is not allowed to carry a timer, so use the object form
  return [{ ...optionsOf([first, second, third]), timer: AUTO_CLOSE_MS }];
};

const popup = (...args) => swal(...withAutoClose(args));
Object.assign(popup, swal); // swal.close(), swal.getState(), swal.stopLoading(), ... keep working

export default popup;
